import cn from 'classnames';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { MultiSelect } from 'primereact/multiselect';
import { Dispatch } from 'react';
import { useTranslation } from 'react-i18next';

import { AllowAccess } from '@features/auth/ControlAccess/AllowAccess/AllowAccess.tsx';
import AdvertiserFilter from '@features/campaigns/list/components/Filters/AdvertiserFilter/AdvertiserFilter.tsx';
import DropdownHeader from '@features/campaigns/list/components/Filters/DropdownHeader/DropdownHeader.tsx';
import { useCampaignListFilters } from '@features/campaigns/list/components/Filters/hooks/useCampaignListFilters.ts';
import PlaceholderWithBadge from '@features/campaigns/list/components/Filters/PlaceholderWithBadge/PlaceholderWithBadge.tsx';
import {
  CampaignListReducerAction,
  CampaignListReducerState,
} from '@features/campaigns/list/types/campaignListReducer.ts';
import { getCampaignCountryOptions } from '@features/campaigns/list/utils/getCampaignCountryOptions.ts';
import { getCampaignTypeOptions } from '@features/campaigns/list/utils/getCampaignTypeOptions.ts';
import { getFormatOptions } from '@features/campaigns/list/utils/getFormatOptions.ts';
import { getStatusOptions } from '@features/campaigns/list/utils/getStatusOptions.ts';
import { FILTERS, FiltersType } from '@features/campaigns/shared/types/filters.ts';
import { i18nNameSpace } from '@shared/consts/i18n';
import { Permissions } from '@shared/consts/permissions.ts';

import styles from './Filters.module.scss';

interface FiltersProps {
  state: CampaignListReducerState;
  dispatch: Dispatch<CampaignListReducerAction>;
  filtersUsed: FiltersType[];
}

function Filters({ state, dispatch, filtersUsed }: FiltersProps) {
  const { t } = useTranslation(i18nNameSpace.CAMPAIGNS);

  const {
    campaignListState,
    filterMinDate,
    filterMaxDate,
    isMinimumOneFormatChecked,
    handleOnSelectAllFormatCheckboxChange,
    handleOnFormatMultiSelectChange,
    handleOnSelectAllStatusCheckboxClick,
    handleOnStatusMultiSelectChange,
    handleOnSelectAllCampaignTypeCheckboxClick,
    handleOnCampaignTypeMultiSelectChange,
    handleOnSelectAllCampaignCountryCheckboxClick,
    handleOnCampaignCountryMultiSelectChange,
    handleOnAdvertiserMultiSelectChange,
    handleOnStartDateCalendarChange,
    handleOnEndDateCalendarChange,
    handleOnClearAllButtonClick,
  } = useCampaignListFilters({ state, dispatch });

  const statusOptions = getStatusOptions(t);
  const typeOptions = getCampaignTypeOptions(t);
  const countryOptions = getCampaignCountryOptions(t);

  const areAllStatusesChecked =
    campaignListState.filters?.status && campaignListState.filters.status?.length === statusOptions.length;
  const isMinimumOneStatusChecked = campaignListState.filters?.status && campaignListState.filters.status.length > 0;
  const isCheckboxAllStatusIndeterminate = Boolean(isMinimumOneStatusChecked && !areAllStatusesChecked);

  const areAllCampaignTypesChecked =
    campaignListState.filters?.type && campaignListState.filters.type.length === typeOptions.length;
  const areAllCampaignCountriesChecked =
    campaignListState.filters?.country && campaignListState.filters.country.length === countryOptions.length;
  const isMinimumOneCampaignCountryChecked =
    campaignListState.filters?.country && campaignListState.filters.country.length > 0;
  const isCheckboxAllCampaignCountryIndeterminate = Boolean(
    isMinimumOneCampaignCountryChecked && !areAllCampaignCountriesChecked,
  );
  const isMinimumOneCampaignTypeChecked = campaignListState.filters?.type && campaignListState.filters.type.length > 0;
  const isCheckboxAllCampaignTypeIndeterminate = Boolean(
    isMinimumOneCampaignTypeChecked && !areAllCampaignTypesChecked,
  );

  const formatOptions = getFormatOptions(t);
  const isSelectAllAvailable = formatOptions.length > 1;
  const areAllFormatsChecked = state.filters?.format && state.filters.format.length === formatOptions.length;
  const isCheckboxAllFormatIndeterminate = Boolean(isMinimumOneFormatChecked && !areAllFormatsChecked);

  return (
    <div className={styles.filtersWrapper}>
      <AllowAccess requiredPermissions={[Permissions.CAMPAIGN_FILTER_BY_ADVERTISER]}>
        <AdvertiserFilter
          selectedValues={campaignListState.filters?.advertiser}
          advertiserLabels={campaignListState.filters?.advertiserLabels}
          onChange={handleOnAdvertiserMultiSelectChange}
        />
      </AllowAccess>
      {filtersUsed.includes(FILTERS.FORMAT) && (
        <MultiSelect
          data-testid={'campaign-format-filter'}
          className={cn(styles.filter, styles.multiSelect)}
          options={formatOptions}
          placeholder={t('page.list.columns.format')}
          onChange={handleOnFormatMultiSelectChange}
          value={state.filters?.format}
          maxSelectedLabels={formatOptions.length}
          aria-label='format filter'
          showSelectAll={isSelectAllAvailable}
          selectedItemTemplate={
            isMinimumOneFormatChecked && (
              <PlaceholderWithBadge
                data={state.filters?.format}
                placeholder={t('page.list.columns.format')}
                prefixId='format'
              />
            )
          }
          panelHeaderTemplate={(event) =>
            isSelectAllAvailable && (
              <DropdownHeader
                data-testid={'format-filter-panel-header'}
                panelEvent={event}
                prefixId='format'
                isIndeterminate={isCheckboxAllFormatIndeterminate}
                handleOnCheckboxChange={handleOnSelectAllFormatCheckboxChange}
              />
            )
          }
          panelClassName={styles.panel}
        />
      )}
      {filtersUsed.includes(FILTERS.START_DATE) && (
        <Calendar
          data-testid={'campaign-start-date-filter'}
          className={styles.filter}
          placeholder={t('page.list.columns.startDate')}
          dateFormat='dd/mm/yy'
          value={state.filters?.startDate}
          viewDate={filterMinDate}
          maxDate={filterMaxDate}
          onChange={handleOnStartDateCalendarChange}
          showButtonBar
          todayButtonClassName={styles.hidden}
          clearButtonClassName={styles.clearButton}
        />
      )}
      {filtersUsed.includes(FILTERS.END_DATE) && (
        <Calendar
          data-testid={'campaign-end-date-filter'}
          className={styles.filter}
          placeholder={t('page.list.columns.endDate')}
          dateFormat='dd/mm/yy'
          value={state.filters?.endDate}
          viewDate={filterMaxDate}
          minDate={filterMinDate}
          onChange={handleOnEndDateCalendarChange}
          showButtonBar
          todayButtonClassName={styles.hidden}
          clearButtonClassName={styles.clearButton}
        />
      )}
      {filtersUsed.includes(FILTERS.STATUS) && (
        <MultiSelect
          data-testid='campaign-status-filter'
          className={cn(styles.filter, styles.multiSelect)}
          options={statusOptions}
          placeholder={t('page.list.columns.status')}
          onChange={handleOnStatusMultiSelectChange}
          value={campaignListState.filters?.status}
          maxSelectedLabels={statusOptions.length}
          aria-label='status filter'
          selectedItemTemplate={
            isMinimumOneStatusChecked && (
              <PlaceholderWithBadge
                data={campaignListState.filters?.status}
                placeholder={t('page.list.columns.status')}
                prefixId='status'
              />
            )
          }
          panelHeaderTemplate={(event) => (
            <DropdownHeader
              data-testid='status-filter-panel-header'
              panelEvent={event}
              prefixId='status'
              isIndeterminate={isCheckboxAllStatusIndeterminate}
              handleOnCheckboxChange={handleOnSelectAllStatusCheckboxClick}
            />
          )}
          panelClassName={styles.panel}
        />
      )}
      <AllowAccess
        requiredPermissions={[Permissions.CAMPAIGN_FILTER_BY_TYPE]}
        additionalCondition={filtersUsed.includes(FILTERS.TYPE)}
      >
        <MultiSelect
          data-testid='campaign-type-filter'
          className={cn(styles.filter, styles.multiSelect)}
          options={typeOptions}
          placeholder={t('page.list.columns.type')}
          onChange={handleOnCampaignTypeMultiSelectChange}
          value={campaignListState.filters?.type}
          maxSelectedLabels={typeOptions.length}
          aria-label='type filter'
          selectedItemTemplate={
            isMinimumOneCampaignTypeChecked && (
              <PlaceholderWithBadge
                data={campaignListState.filters?.type}
                placeholder={t('page.list.columns.type')}
                prefixId='type'
              />
            )
          }
          panelHeaderTemplate={(event) => (
            <DropdownHeader
              data-testid='type-filter-panel-header'
              panelEvent={event}
              prefixId='type'
              isIndeterminate={isCheckboxAllCampaignTypeIndeterminate}
              handleOnCheckboxChange={handleOnSelectAllCampaignTypeCheckboxClick}
            />
          )}
          panelClassName={styles.panel}
        />
      </AllowAccess>
      <AllowAccess
        requiredPermissions={[Permissions.CAMPAIGN_FILTER_BY_COUNTRY]}
        additionalCondition={filtersUsed.includes(FILTERS.COUNTRY)}
      >
        <MultiSelect
          data-testid='campaign-country-filter'
          className={cn(styles.filter, styles.multiSelect)}
          options={countryOptions}
          placeholder={t('page.list.columns.country')}
          onChange={handleOnCampaignCountryMultiSelectChange}
          value={campaignListState.filters?.country}
          maxSelectedLabels={countryOptions.length}
          aria-label='country filter'
          selectedItemTemplate={
            isMinimumOneCampaignCountryChecked && (
              <PlaceholderWithBadge
                data={campaignListState.filters?.country}
                placeholder={t('page.list.columns.country')}
                prefixId='country'
              />
            )
          }
          panelHeaderTemplate={(event) => (
            <DropdownHeader
              data-testid='country-filter-panel-header'
              panelEvent={event}
              prefixId='country'
              isIndeterminate={isCheckboxAllCampaignCountryIndeterminate}
              handleOnCheckboxChange={handleOnSelectAllCampaignCountryCheckboxClick}
            />
          )}
          panelClassName={styles.panel}
        />
      </AllowAccess>
      <Button
        data-testid={'clear-all-campaign-filters-button'}
        text
        label={t('page.clearAll')}
        onClick={handleOnClearAllButtonClick}
      />
    </div>
  );
}

export default Filters;
